/**
 * DOCUMENTATION:
 * Component: CssFundamentals
 * Purpose: Educational page covering the absolute core mechanics of CSS: Box Model, Specificity, and Units.
 * * * Note for Junior Developers:
 * - If you don't understand the Box Model, your layouts will always break.
 * - If you don't understand Specificity, you will end up writing `!important` everywhere (which is terrible practice).
 * - If you don't understand Relative Units (`rem`, `em`), your site will not be accessible to users who change their browser font size.
 * - As always, we separate our data (`coreConcepts`, `codingModules`) from our presentation using `useMemo`.
 */

import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Card,
    Typography,
    Grid,
    Avatar,
    Button,
    styled,
    useTheme,
    useMediaQuery,
    Fab,
    Zoom,
    Tooltip,
    Stack,
} from "@mui/material";

/* ✅ Icons */
import { AiFillBulb, AiOutlineRead } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

/* ✅ Assets */
const bgImage = "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5";

/* ✅ Styled Components - Clean Code Principles */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
    [theme.breakpoints.down("sm")]: {
        paddingTop: theme.spacing(8),
    },
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
}));

const CodeContainer = styled(Box)(({ theme }) => ({
    position: "relative",
    marginTop: theme.spacing(2),
}));

const CodeBlock = styled("pre")(({ theme }) => ({
    backgroundColor: "#0d1117",
    color: "#c9d1d9",
    padding: "20px",
    borderRadius: "10px",
    overflowX: "auto",
    fontSize: "0.9rem",
    lineHeight: 1.6,
    border: "1px solid rgba(255,255,255,0.05)",
    fontFamily: "'Fira Code', 'Courier New', monospace",
}));

const ResponsiveDataRow = styled(Box)(({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "1fr 2fr",
    gap: "15px",
    padding: "12px",
    borderBottom: "1px solid rgba(255,255,255,0.1)",
    "&:last-child": { borderBottom: "none" },
    [theme.breakpoints.down("md")]: {
        gridTemplateColumns: "1fr",
        gap: "5px",
        padding: "15px",
    },
}));

const ActionButton = styled(Button)(({ theme }) => ({
    backgroundColor: "goldenrod",
    color: "#100720",
    fontWeight: 800,
    padding: "12px 40px",
    borderRadius: "999px",
    transition: "0.3s",
    textTransform: "uppercase",
    "&:hover": {
        backgroundColor: "#e0b200",
        transform: "scale(1.05)",
    },
}));

/* ✅ Main Component */
export default function CssFundamentals() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    /* UI State */
    const [showScroll, setShowScroll] = useState(false);
    const [copiedIndex, setCopiedIndex] = useState(null);

    /* Scroll Listener for FAB */
    useEffect(() => {
        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    const handleScrollTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    /* Copy to Clipboard Handler */
    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    /* ---------------- DATA DEFINITIONS ---------------- */

    // Core CSS Concepts Table
    const coreConcepts = useMemo(() => [
        { concept: "The Box Model", description: "Every HTML element is a rectangular box. It consists of: Margins (outer space), Borders, Padding (inner space), and the actual Content." },
        { concept: "Specificity", description: "The algorithm browsers use to determine which CSS rule applies when multiple rules target the same element. IDs beat Classes. Classes beat Elements." },
        { concept: "Absolute Units (px)", description: "Pixels (px) are fixed. A 16px font is always 16px. Overusing px creates rigid sites that fail accessibility standards for visually impaired users." },
        { concept: "Relative Units (rem, em, %)", description: "These units scale based on parent elements or the root browser font size. They are the secret to fluid, responsive design." },
    ], []);

    // Coding examples demonstrating CSS fundamentals
    const codingModules = useMemo(() => [
        {
            title: "1. Taming the Box Model (Border-Box)",
            code: `/* ❌ BAD: Default Content-Box */\n.box-bad {\n  width: 200px;\n  padding: 20px;\n  /* The actual rendered width becomes 240px! (200 + 20L + 20R) */\n}\n\n/* ✅ GOOD: The Universal Fix */\n*, *::before, *::after {\n  box-sizing: border-box;\n}\n\n.box-good {\n  width: 200px;\n  padding: 20px;\n  /* The rendered width stays exactly 200px. Padding pushes INWARD. */\n}`,
            desc: "By default, adding padding or borders makes an element wider than the width you set. Applying `box-sizing: border-box` globally fixes this mathematical headache permanently."
        },
        {
            title: "2. The Specificity Wars",
            code: `/* Specificity Score: 0, 0, 1 (One Element tag) */\np {\n  color: white;\n}\n\n/* Specificity Score: 0, 1, 0 (One Class) -> WINS over 'p' */\n.highlight {\n  color: goldenrod;\n}\n\n/* Specificity Score: 1, 0, 0 (One ID) -> WINS over '.highlight' */\n#main-text {\n  color: red;\n}\n\n/* ☢️ NUCLEAR OPTION (Avoid at all costs) -> WINS over everything */\np {\n  color: blue !important;\n}`,
            desc: "If a style isn't applying, you likely have a specificity conflict. Instead of using `!important` (which breaks the natural cascade), write more specific selectors or reorganize your CSS file."
        },
        {
            title: "3. Modern Typography: REM vs EM",
            code: `/* Assume browser default font-size is 16px */\n\n/* REM (Root EM): Always relative to the <html> root element */\n.title {\n  font-size: 2rem; /* 2 * 16px = 32px */\n}\n\n/* EM: Relative to its direct parent element (Be careful, it compounds!) */\n.parent {\n  font-size: 20px;\n}\n.parent .child {\n  font-size: 2em; /* 2 * 20px = 40px */\n}`,
            desc: "Always use `rem` for font sizes and spacing. It respects the user's browser settings (great for accessibility) and prevents the terrifying 'compounding' effect that happens when you nest elements using `em`."
        }
    ], []);

    return (
        <PageWrapper>
            {/* ✅ Page Header */}
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography
                    variant={isMobile ? "h5" : "h3"}
                    sx={{
                        fontWeight: 800,
                        color: "goldenrod",
                        display: "inline-block",
                        px: { xs: 3, md: 6 },
                        py: 1.5,
                        borderRadius: "999px",
                        boxShadow: "0 6px 20px rgba(218, 165, 32, 0.3)",
                    }}
                >
                    CSS FUNDAMENTALS
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                {/* ✅ Theory Section */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            The Rules of the Cascade
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.9, lineHeight: 1.6 }}>
                            CSS (Cascading Style Sheets) is not a programming language like JavaScript; it is a declarative layout language. You declare *what* things should look like, and the browser's rendering engine figures out the rest. To control the engine, you must master how it measures space (The Box Model), how it resolves conflicts (Specificity), and how it scales across devices (Units).
                        </Typography>

                        {/* ✅ Core Concepts Table */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 4, mb: 2 }}>
                            <Avatar sx={{ bgcolor: "goldenrod", color: "#100720" }}>
                                <AiFillBulb size={24} />
                            </Avatar>
                            <Typography variant="h6" fontWeight={700}>The Three Pillars of CSS</Typography>
                        </Box>

                        <Box sx={{ bgcolor: "rgba(255,255,255,0.03)", borderRadius: "10px", overflow: "hidden" }}>
                            {!isMobile && (
                                <ResponsiveDataRow sx={{ bgcolor: "rgba(218, 165, 32, 0.1)", color: "goldenrod", fontWeight: "bold" }}>
                                    <div>Concept</div>
                                    <div>Engineering Description</div>
                                </ResponsiveDataRow>
                            )}
                            {coreConcepts.map((item, i) => (
                                <ResponsiveDataRow key={i}>
                                    <Box sx={{ fontWeight: "bold", color: "goldenrod" }}>{item.concept}</Box>
                                    <Box sx={{ opacity: 0.9, fontSize: "0.9rem", lineHeight: 1.5 }}>{item.description}</Box>
                                </ResponsiveDataRow>
                            ))}
                        </Box>
                    </SectionCard>
                </Grid>

                {/* ✅ Coding Modules Section */}
                {codingModules.map((item, i) => (
                    <Grid item xs={12} key={i}>
                        <SectionCard>
                            {/* Module Header & Action Buttons */}
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <Avatar sx={{ bgcolor: "rgba(218, 165, 32, 0.2)", color: "goldenrod" }}>
                                        <AiOutlineRead size={24} />
                                    </Avatar>
                                    <Typography variant="h6" fontWeight={700}>
                                        {item.title}
                                    </Typography>
                                </Box>

                                <Stack direction="row" spacing={1}>
                                    <Tooltip title={copiedIndex === i ? "Copied!" : "Copy Code"}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => handleCopy(item.code, i)}
                                            sx={{
                                                color: "white",
                                                borderColor: "rgba(255,255,255,0.3)",
                                                "&:hover": { borderColor: "white" }
                                            }}
                                        >
                                            {copiedIndex === i ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                        </Button>
                                    </Tooltip>
                                    <Link to="/compiler" style={{ textDecoration: "none" }}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<CodeIcon />}
                                            sx={{
                                                color: "goldenrod",
                                                borderColor: "goldenrod",
                                                "&:hover": { borderColor: "#e0b200", bgcolor: "rgba(218, 165, 32, 0.1)" }
                                            }}
                                        >
                                            Try it
                                        </Button>
                                    </Link>
                                </Stack>
                            </Box>

                            <Typography variant="body2" sx={{ mb: 2, opacity: 0.8, lineHeight: 1.6 }}>{item.desc}</Typography>

                            {/* Code Block Presentation */}
                            <CodeContainer>
                                <CodeBlock>{item.code}</CodeBlock>
                            </CodeContainer>
                        </SectionCard>
                    </Grid>
                ))}

                {/* ✅ Footer Navigation */}
                <Grid item xs={12} textAlign="center" sx={{ mt: 4 }}>
                    <Link to="/css-dashboard" style={{ textDecoration: "none" }}>
                        <ActionButton variant="contained">
                            BACK TO DASHBOARD
                        </ActionButton>
                    </Link>
                </Grid>
            </Grid>

            {/* ✅ Floating Back to Top Button */}
            <Zoom in={showScroll}>
                <Fab
                    size="small"
                    onClick={handleScrollTop}
                    sx={{ position: "fixed", bottom: 25, right: 25, bgcolor: "goldenrod", "&:hover": { bgcolor: "#e0b200" } }}
                >
                    <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                </Fab>
            </Zoom>
        </PageWrapper>
    );
}
